import api, { togglePostStatus, togglePostTrending } from "./api";
import { POSTS_URL, POSTS_STORE_URL, postUrl, postUpdateUrl, postDeleteUrl } from "./api";

/**
 * Fetch every post.
 * GET /api/todattalkspost -> { success, message, data: [...] }
 * Each item already comes with its nested `category` object.
 */
export async function fetchPosts() {
  const res = await api.get(POSTS_URL);
  const payload = res.data || {};
  return Array.isArray(payload.data) ? payload.data : [];
}

/**
 * Fetch a single post by id.
 * GET /api/todattalkspost/{id}
 */
export async function fetchPostById(id) {
  const res = await api.get(postUrl(id));
  return res.data?.data;
}

/**
 * Build the multipart/form-data payload for create/update.
 * `imageFile` (a File from an <input type="file">) is sent as `image` —
 * only attached when the user actually picked/changed a file, so an
 * edit without a new image keeps the current one.
 */
function buildFormData(post) {
  const fd = new FormData();
  fd.append("user_id", post.user_id);
  fd.append("category_id", post.category_id);
  fd.append("title", post.title ?? "");
  fd.append("summary", post.summary ?? "");
  fd.append("description", post.description ?? "");
  fd.append("tags", Array.isArray(post.tags) ? post.tags.join(",") : post.tags ?? "");
  fd.append("status", post.status ? "1" : "0");
  fd.append("trending", post.trending ? "1" : "0");

  // if (post.imageFile instanceof File) {
  //   fd.append("imageUrl", post.imageFile);
  // } else if (post.imageUrl) {
  //   // No new file picked — keep whatever image path the post already has.
  //   fd.append("imageUrl", post.imageUrl);
  // }

  if (post.imageFile instanceof File) {
    fd.append("imageUrl", post.imageFile);
  }

  return fd;
}

/**
 * Create a new post.
 * POST /api/todattalkspost/store (multipart/form-data)
 */
export async function createPost(post) {
  const res = await api.post(POSTS_STORE_URL, buildFormData(post), {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

/**
 * Update an existing post. The API's `title` field is required on
 * update, so callers should always pass the full current post merged
 * with whatever changed (this is what quick actions like the status/
 * trending toggles below do).
 * PUT /api/todattalkspost/update/{id} (multipart/form-data)
 */
export async function updatePost(id, post) {
  const res = await api.post(postUpdateUrl(id), buildFormData(post), {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

/**
 * Delete a post.
 * DELETE /api/todattalkspost/delete/{id}
 */
export async function deletePost(id) {
  const res = await api.delete(postDeleteUrl(id));
  return res.data;
}

/**
 * Flip just the `status` flag. There's no dedicated toggle route in this
 * API, so this resends the full post (required by the update validator)
 * with only `status` changed.
 */
export async function toggleStatus(post, nextStatus) {
  const res = await api.get(togglePostStatus(post.id, nextStatus ? 1 : 0));
  return res.data;
}

/**
 * Flip just the `trending` flag — same approach as toggleStatus.
 */
export async function toggleTrending(post, nextTrending) {
  const res = await api.get(togglePostTrending(post.id, nextTrending ? 1 : 0));
  return res.data;
}

/**
 * Tags come back as a comma separated string ("technology,ai,news").
 * This safely normalizes it (and tolerates legacy JSON-encoded arrays
 * too) down to a flat array of plain strings for display / editing.
 */
export function parseTags(raw) {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t).trim()).filter(Boolean);
  }
  if (typeof raw !== "string" || !raw.trim()) return [];

  let value = raw;
  for (let i = 0; i < 5; i++) {
    if (typeof value !== "string") break;
    try {
      value = JSON.parse(value);
    } catch {
      break;
    }
  }
  if (Array.isArray(value)) {
    return value.flat(Infinity).map((t) => String(t).trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}
